import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

const TOTAL_MS = 1100;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function VelkoSplash() {
  // Plays on every cold mount of the layout (= page load / hard refresh).
  // Intentionally not gated by sessionStorage — matches Mockup A behavior.
  const [show, setShow] = useState<boolean>(true);
  const reduced = prefersReducedMotion();

  useEffect(() => {
    const id = window.setTimeout(() => setShow(false), reduced ? 200 : TOTAL_MS);
    return () => window.clearTimeout(id);
  }, [reduced]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="velko-splash"
          aria-hidden="true"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-7 bg-charcoal-950"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.svg
            viewBox="0 0 64 64"
            className="size-[88px]"
            initial={{ opacity: reduced ? 0 : 1 }}
            animate={{ opacity: 1 }}
            transition={{ duration: reduced ? 0.15 : 0 }}
          >
            <motion.path
              d="M14 10 L17 6 L20 10 L23 6 L26 10 L29 6 L32 10 L35 6 L38 10 L41 6 L44 10 L47 6 L50 10 L50 52 L47 58 L44 52 L41 58 L38 52 L35 58 L32 52 L29 58 L26 52 L23 58 L20 52 L17 58 L14 52 Z"
              initial={reduced ? { fill: '#1a1a2e' } : { fill: 'transparent' }}
              animate={{ fill: '#1a1a2e', strokeWidth: 0 }}
              stroke="#9e9eb8"
              strokeWidth={reduced ? 0 : 1.5}
              transition={{ duration: reduced ? 0.15 : 0.4, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              style={{ pathLength: reduced ? 1 : undefined }}
            />
            <motion.rect
              x={19}
              y={15}
              width={14}
              height={2.5}
              rx={1}
              fill="#FFFFFF"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: reduced ? 0.05 : 0.38 }}
            />
            <motion.rect
              x={19}
              y={22}
              width={26}
              height={6}
              rx={1.5}
              fill="#A3E635"
              initial={{ opacity: 0, scale: 1 }}
              animate={
                reduced
                  ? { opacity: 1, scale: 1 }
                  : {
                      opacity: [0, 1, 1, 1],
                      scale: [1, 1, 1.08, 1],
                      filter: [
                        'drop-shadow(0 0 0 rgba(163,230,53,0))',
                        'drop-shadow(0 0 0 rgba(163,230,53,0))',
                        'drop-shadow(0 0 8px rgba(163,230,53,0.7))',
                        'drop-shadow(0 0 4px rgba(163,230,53,0.3))',
                      ],
                    }
              }
              transition={{
                duration: reduced ? 0.15 : 0.6,
                delay: reduced ? 0.05 : 0.38,
                ease: [0.34, 1.56, 0.64, 1],
              }}
              style={{ transformOrigin: '32px 25px' }}
            />
            <motion.rect
              x={19}
              y={33}
              width={20}
              height={2.5}
              rx={1}
              fill="#FFFFFF"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: reduced ? 0.05 : 0.44 }}
            />
            <motion.rect
              x={19}
              y={40}
              width={26}
              height={3}
              rx={1}
              fill="#FFFFFF"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: reduced ? 0.05 : 0.5 }}
            />
            <motion.rect
              x={19}
              y={47}
              width={18}
              height={2}
              rx={1}
              fill="#FFFFFF"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: reduced ? 0.05 : 0.56 }}
            />
          </motion.svg>
          <motion.div
            className="flex font-heading text-[22px] font-bold text-white"
            initial={
              reduced
                ? { opacity: 1, letterSpacing: '0.16em' }
                : { opacity: 0, letterSpacing: '0.4em' }
            }
            animate={{ opacity: 1, letterSpacing: '0.16em' }}
            transition={{
              duration: reduced ? 0.15 : 0.35,
              delay: reduced ? 0.05 : 0.72,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <span>V</span>
            <span>E</span>
            <span>L</span>
            <span>K</span>
            <span>O</span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
